import useGroupStore from "@/store/group";
import { usePayStore } from "@/store/pay";
import { useUserStore } from "@/store/user";
import dayjs from "dayjs";
import type { ActivityConfig, UserSegment } from "@/views/power/components/VipPackageActivity/types";
import {
  buildActivityParticipationFlags,
  resolveActivityDiscount,
  resolveActivityPricingContext,
  resolveActivityPricingEntry,
  shouldSkipActivityPricing
} from "@/assets/pay/activityPricing";

// 会员每月按照31天计算
export const packageMonthDays = 31;

// 会员半月月按照15天计算
export const packageHalfMonthDays = 15;

// 单独购买固定折扣
export const discount = 0.8;

/** VVIP 旗舰版固定价格表（不走通用折扣公式，由产品文档直接指定） */
export const ULTRA_ORIGIN_PRICE: Record<string, number> = {
  month: 429,
  season: 1289,
  year: 5139
};

export const ULTRA_SALE_PRICE: Record<string, number> = {
  month: 339,
  season: 959,
  year: 3599
};

/** B 组 SVIP 月度连续订阅展示价（前端写死，实付以后端商品为准） */
export const SVIP_MONTHLY_SUBSCRIBE_B_ORIGIN_PRICE = 199;

export const SVIP_MONTHLY_SUBSCRIBE_B_SALE_PRICE = {
  first: 158,
  renew: 174
} as const;

/**
 * 专业版连续订阅角标：B 组看 SVIP 首期低价购买记录，A 组仍看 firstSign
 */
export function resolveSvipSubscribeOfferLabel(hasFirstPurchaseOffer: boolean): string {
  return hasFirstPurchaseOffer ? "首购8折+续费8.8折" : "续费8.8折";
}

/** 临期 7 天内续费时，旗舰版年度 SKU 售价（6.9 折，产品文档 3539 元） */
export const ULTRA_RENEWAL_YEAR_SALE_PRICE = 3539;

// 套餐列表
export enum packageTimeEnum {
  "week" = "7天",
  "halfSubscribe" = "连续包半月",
  "subscribe" = "连续包月",
  /** VVIP 月度买断（与 Tab「按月」对应，非连续订阅） */
  "month" = "月度",
  "season" = "季度",
  "year" = "年度",
  "year_v1" = "年度（再送2月）"
}

export enum _packageTimeEnum {
  "week" = "7天",
  "halfSubscribe" = "连续包半月",
  "subscribe" = "连续包月",
  "season" = "按季购买",
  "year" = "按年购买",
  "year_v1" = "年度（再送2月）"
}

export enum VipTimePackageUnitEnum {
  "week" = "7天",
  "subscribe" = "月",
  "halfSubscribe" = "半月",
  "season" = "季",
  "year" = "年"
}

export enum VipTimePackageMonthEnum {
  "week" = 7 / packageMonthDays,
  "subscribe" = 1,
  "halfSubscribe" = 0.5,
  "month" = 1,
  "season" = 3,
  "year" = 12,
  "year_v1" = 14
}

export type IPowerPackageKey = "500" | "1000" | "2000";
export type TeamPowerPackagePayKey = `package_team_${"pro" | "plus" | "ultra"}_${number}`;
export type PowerPackagePayKey = IPowerPackageKey | TeamPowerPackagePayKey;

export type IPersonalPackageKey = "vip" | "svip" | "ultra" | "free";

// 算力加量包套餐
export const powerPackage: Record<IPowerPackageKey, any> = {
  "500": {
    money: 50,
    basePower: 500,
    vipExtraPower: 125,
    svipExtraPower: 500,
    /** 旗舰版：算力充值额外加赠 125%（按包基数折算） */
    ultraExtraPower: 625
  },
  "1000": {
    money: 100,
    basePower: 1000,
    vipExtraPower: 250,
    svipExtraPower: 1000,
    ultraExtraPower: 1250
  },
  "2000": {
    money: 200,
    basePower: 2000,
    vipExtraPower: 500,
    svipExtraPower: 2000,
    ultraExtraPower: 2500
  }
};

/**
 * 区分个人版和团队版处理逻辑
 */
export function getPowerPackageNumber(key: PowerPackagePayKey) {
  // 特殊处理购买团队版本的会员算力 package_team_{pro|plus|ultra}_【xxx】，即购买xxx算力
  if (/^package_team_(pro|plus|ultra)_\d+$/.test(key)) {
    return parseInt(key.split("_")[3]);
  }

  const userStore = useUserStore();
  let powerState = powerPackage[key as IPowerPackageKey];
  if (!userStore.isGroupLogin) {
    switch (userStore.highestPackage) {
      case "vip":
        return powerState.basePower + powerState.vipExtraPower;
      case "svip":
        return powerState.basePower + powerState.svipExtraPower;
      case "ultra":
        return powerState.basePower + powerState.ultraExtraPower;
      default:
        return powerState.basePower;
    }
  } else {
    const groupStore = useGroupStore();
    switch (groupStore.memberType) {
      case "pro":
        return powerState.basePower + powerState.vipExtraPower;
      case "plus":
        return powerState.basePower + powerState.svipExtraPower;
      case "ultra":
        return powerState.basePower + powerState.ultraExtraPower;
      default:
        return powerState.basePower;
    }
  }
}

// 获取用户下单时的key值
export function getUserPowerPackagePayKey(key: IPowerPackageKey) {
  const userStore = useUserStore();

  switch (userStore.highestPackage) {
    case "free":
      return `package_free_${getPowerPackageNumber(key)}`;
    case "vip":
      return `package_pro_${getPowerPackageNumber(key)}`;
    case "svip":
      return `package_plus_${getPowerPackageNumber(key)}`;
    case "ultra":
      return `package_ultra_${getPowerPackageNumber(key)}`;
  }
}

/**
 * 补充团队模式下过期状态的问题
 */
export function getTeamUserPowerPackagePayKey(key: IPowerPackageKey): TeamPowerPackagePayKey {
  const groupStore = useGroupStore();

  switch (groupStore.memberType) {
    case "pro":
      return `package_team_pro_${getPowerPackageNumber(key)}`;
    case "plus":
      return `package_team_plus_${getPowerPackageNumber(key)}`;
    case "ultra":
      return `package_team_ultra_${getPowerPackageNumber(key)}`;
    default:
      return `package_team_pro_${getPowerPackageNumber(key)}`;
  }
}

// 免费版
const freePackage = {
  free: {
    label: "免费版",
    perMonth: 0, // 每月套餐价格
    power: 30,
    powerLabel: "100 算力（注册后系统自动赠送）",
    groupPowerLabel: "100 算力（注册后系统自动赠送）",
    space: 1, // GB
    orderKey: "",
    historyPreservationPeriod: "7天"
  }
};

export enum vipPackageEnum {
  "pro" = "vip",
  "plus" = "svip",
  "ultra" = "ultra",
  "free" = "free"
}

export enum _vipPackageEnum {
  "vip" = "pro",
  "svip" = "plus",
  "ultra" = "ultra",
  "free" = "free"
}

// vip套餐
export const vipPackage: Record<IPersonalPackageKey, any> = {
  vip: {
    vipType: "vip",
    label: "标准版",
    power: 3000,
    powerLabel: "每月3000算力",
    groupPowerLabel: "每月3000算力",
    space: 20, // GB
    parallelTask: 2, // 并行任务
    orderKey: "pro",
    historyPreservationPeriod: "会员时长内无限期保存"
  },
  svip: {
    vipType: "svip",
    label: "专业版",
    power: 9000,
    powerLabel: "每月9000算力",
    groupPowerLabel: "每月9000算力",
    space: 80, // GB
    parallelTask: 4, // 并行任务
    orderKey: "plus",
    historyPreservationPeriod: "会员时长内无限期保存"
  },
  ultra: {
    vipType: "ultra",
    label: "旗舰版",
    power: 20000,
    powerLabel: "每月20000算力",
    groupPowerLabel: "每月20000算力",
    space: 500, // GB
    parallelTask: 6,
    orderKey: "ultra",
    historyPreservationPeriod: "会员时长内无限期保存"
  },
  free: {
    vipType: "free",
    label: "免费版",
    power: 30,
    powerLabel: "免费 100算力",
    groupPowerLabel: "-",
    space: 1, // GB
    orderKey: "",
    historyPreservationPeriod: "7天"
  }
};

// 用户套餐
export const userPackage = Object.assign(freePackage, vipPackage);

/** 团队版会员中心权益对比展示的套餐档位（标准 / 专业 / 旗舰，不含免费版） */
export const TEAM_PACKAGE_KEYS = ["vip", "svip", "ultra"] as const;

export type ITeamPackageKey = (typeof TEAM_PACKAGE_KEYS)[number];

/** 团队版会员中心权益对比用套餐（标准 / 专业 / 旗舰） */
export const teamUserPackage: Pick<typeof userPackage, ITeamPackageKey> = {
  vip: userPackage.vip,
  svip: userPackage.svip,
  ultra: userPackage.ultra
};

// 获取套餐信息
export function getPackageInfo(key: any) {
  if (userPackage[key as keyof typeof userPackage]) {
    return userPackage[key as keyof typeof userPackage];
  }

  const [target] = Object.values(userPackage).filter((item) => item.orderKey === key);
  return target;
}

export interface PackageMoneyOptions {
  origin?: boolean;
  details?: boolean;
  isRenew?: boolean;
  isShowActivity?: boolean;
  isParticipatedPro?: boolean;
  isParticipatedPlus?: boolean;
  activityUpgradePlusMonth?: boolean;
  activityUpgradeUltraMonth?: boolean;
  activityUpgradePlusYear?: boolean;
  activityUpgradeUltraYear?: boolean;
  activityConfig?: ActivityConfig;
  activitySegment?: UserSegment;
  currentPackage?: string;
  /** B 组 SVIP 月度连续订阅展示价（仅 UI，实付以后端为准） */
  isSvipMonthlySubscribeB?: boolean;
}

// 获取会员套餐折扣
export function getTimeModeDiscount(
  timeMode: keyof typeof packageTimeEnum,
  options: {
    /** 套餐类型 */
    vipType?: keyof typeof vipPackage;
    // 是否续费
    isRenew?: boolean;
    // 是否展示活动
    isShowActivity?: boolean;
    // 是否参与过活动Pro套餐
    isParticipatedPro?: boolean;
    // 是否参与过活动Plus套餐
    isParticipatedPlus?: boolean;
    activityUpgradePlusMonth?: boolean;
    activityUpgradeUltraMonth?: boolean;
    activityUpgradePlusYear?: boolean;
    activityUpgradeUltraYear?: boolean;
    activityConfig?: ActivityConfig;
    activitySegment?: UserSegment;
    currentPackage?: string;
  } = {}
) {
  const payStore = usePayStore();
  const userStore = useUserStore();
  const vipType = options.vipType;

  /** 是否续费场景 */
  const isRenew = options.isRenew || false;
  /** 是否是试用会员 */
  const isTryMemberUser = userStore.isTryMemberUser;
  /** 是否拥有首次订阅优惠 */
  const hasSubscribeDiscount = payStore.hasSubscribeDiscount;
  /** 是否有活动 */
  const hasActivity = !!options.isShowActivity;
  const participationFlags = buildActivityParticipationFlags(options);

  if (hasActivity) {
    const { config, segment, currentPackage } = resolveActivityPricingContext(options, userStore);
    if (!shouldSkipActivityPricing(timeMode, vipType, participationFlags, currentPackage)) {
      const activityDiscount = resolveActivityDiscount(config, segment, timeMode, {
        packageKey: vipType,
        currentPackage
      });
      if (activityDiscount !== null) return activityDiscount;
    }
  }

  switch (timeMode) {
    case "week":
      return 1;
    case "halfSubscribe":
      // 试用会员且有首次订阅优惠时，首购88折
      if (isTryMemberUser && hasSubscribeDiscount) return 0.88;

      // 续费时88折
      if (isRenew) return 0.88;

      // 首购时8折
      if (hasSubscribeDiscount) return 0.8;

      return 0.88;
    case "subscribe":
      // 连续包月续费固定 8.8 折（标准版 ¥87 / 与需求「续费¥87/月」一致）
      if (isRenew) return 0.88;

      // 试用会员或无首购优惠时按原价；有首购优惠时 8 折
      if (isTryMemberUser || !hasSubscribeDiscount) return 1;

      return 0.8;
    case "season":
      return 0.88;
    case "year":
      return 0.7;
    case "year_v1":
      return 0.6;

    default:
      return 1;
  }
}

/**
 * 处理折扣显示，例如 0.6 显示为 6 折，0.55 显示55折
 */
export function getDiscountLabel(discount: number) {
  const discountNum = Math.round(discount * 100);
  return String(discountNum).replace(/0$/, "");
}

/**
 * 根据原价与售价计算折扣文案，例如 "7.9折 立省９０元"
 * @returns 折扣文案；原价 ≤ 售价时返回 null
 */
export function formatDiscountOffer(origin: number, sale: number): string | null {
  if (origin <= sale) return null;
  const discountLabel = (Math.round((sale / origin) * 100) / 10).toFixed(1).replace(/\.0$/, "");
  return `${discountLabel}折 立省${origin - sale}元`;
}

// 获取套餐原价
export function getPackageOrigin(
  key: keyof typeof vipPackage,
  timeMode: keyof typeof packageTimeEnum,
  options: Pick<PackageMoneyOptions, 'isSvipMonthlySubscribeB'> = {}
) {
  if (key === "ultra") {
    const m = timeMode === "season" ? "season"
      : timeMode === "year" || timeMode === "year_v1" ? "year"
      : "month";
    return ULTRA_ORIGIN_PRICE[m];
  }

  if (key === "vip") {
    switch (timeMode) {
      case "week":
        return 25;
      case "subscribe":
        return 99;
      case "season":
        return 297;
      case "year":
        return 1188;
    }
  }

  switch (timeMode) {
    case "halfSubscribe":
    case "subscribe":
      if (key === "svip" && options.isSvipMonthlySubscribeB && timeMode === "subscribe") {
        return SVIP_MONTHLY_SUBSCRIBE_B_ORIGIN_PRICE;
      }
      return 99;
    case "season":
      return 597;
    case "year":
      return 2388;
  }

  return 2388;
}

/**
 * 获取开通vip金额
 * @param key 套餐类型
 * @param timeMode 套餐周期
 * @param options 选项 isRenew 是否续费，处理半月折扣
 * @returns 金额
 */
export function getPackageMoney(
  key: keyof typeof vipPackage,
  timeMode: keyof typeof packageTimeEnum,
  {
    origin = false,
    details = false,
    isRenew = false,
    isShowActivity = false,
    isParticipatedPro = false,
    isParticipatedPlus = false,
    activityUpgradePlusMonth = false,
    activityUpgradeUltraMonth = false,
    activityUpgradePlusYear = false,
    activityUpgradeUltraYear = false,
    activityConfig,
    activitySegment,
    currentPackage,
    isSvipMonthlySubscribeB
  }: PackageMoneyOptions = {}
) {
  const userStore = useUserStore();
  const pricingOptions: PackageMoneyOptions = {
    isRenew,
    isShowActivity,
    isParticipatedPro,
    isParticipatedPlus,
    activityUpgradePlusMonth,
    activityUpgradeUltraMonth,
    activityUpgradePlusYear,
    activityUpgradeUltraYear,
    activityConfig,
    activitySegment,
    currentPackage,
    isSvipMonthlySubscribeB
  };
  const participationFlags = buildActivityParticipationFlags(pricingOptions);
  const { currentPackage: resolvedCurrentPackage } = resolveActivityPricingContext(pricingOptions, userStore);

  // B 组 SVIP 月度连续订阅：固定展示价（不走通用折扣公式）
  if (
    key === "svip" &&
    timeMode === "subscribe" &&
    isSvipMonthlySubscribeB &&
    !isShowActivity
  ) {
    if (origin) {
      return SVIP_MONTHLY_SUBSCRIBE_B_ORIGIN_PRICE;
    }
    if (details || isRenew) {
      return SVIP_MONTHLY_SUBSCRIBE_B_SALE_PRICE.renew;
    }
    // B 组首购 158 看 SVIP 首期低价购买记录，不复用 VIP 的 firstSign
    return userStore.canBuySvipFirstPurchaseLowPrice
      ? SVIP_MONTHLY_SUBSCRIBE_B_SALE_PRICE.first
      : SVIP_MONTHLY_SUBSCRIBE_B_SALE_PRICE.renew;
  }

  // VVIP：产品文档固定价（不走通用折扣公式）；Tab 仍可能传 subscribe，此处统一映射为 month
  if (key === "ultra") {
    const m = timeMode === "season" ? "season"
      : timeMode === "year" || timeMode === "year_v1" ? "year"
      : "month";
    if (origin) {
      return ULTRA_ORIGIN_PRICE[m];
    }
    if (isShowActivity && !shouldSkipActivityPricing(timeMode, key, participationFlags, resolvedCurrentPackage)) {
      const { config, segment } = resolveActivityPricingContext(pricingOptions, userStore);
      const entry = resolveActivityPricingEntry(config, segment, timeMode, {
        packageKey: key,
        currentPackage: resolvedCurrentPackage
      });
      if (entry?.type === "fixed") return entry.value;
      if (entry?.type === "multiplier") return Math.round(ULTRA_ORIGIN_PRICE[m] * entry.value);
    }
    return ULTRA_SALE_PRICE[m];
  }

  // 特殊转换"year_v1",year_v1与year价格一直,只是多送2个月
  const _timeMode = timeMode == "year_v1" ? "year" : timeMode;
  const originMoney = getPackageOrigin(key, _timeMode, { isSvipMonthlySubscribeB });
  let result = 0;

  if (
    !origin &&
    isShowActivity &&
    !shouldSkipActivityPricing(timeMode, key, participationFlags, resolvedCurrentPackage)
  ) {
    const { config, segment } = resolveActivityPricingContext(pricingOptions, userStore);
    const entry = resolveActivityPricingEntry(config, segment, timeMode, {
      packageKey: key,
      currentPackage: resolvedCurrentPackage
    });
    if (entry?.type === "fixed") {
      return entry.value;
    }
  }

  if (details && ["halfSubscribe", "subscribe"].includes(timeMode)) {
    const discount = getTimeModeDiscount(_timeMode, {
      vipType: key,
      isRenew: true,
      isShowActivity: false
    });
    result = Math.round(originMoney * discount);
  } else if (origin) {
    result = originMoney;
  } else if (_timeMode === "week") {
    result = 9.9;
  } else {
    const discount = getTimeModeDiscount(_timeMode, {
      vipType: key,
      isRenew,
      isShowActivity,
      isParticipatedPro,
      isParticipatedPlus,
      activityUpgradePlusMonth,
      activityUpgradeUltraMonth,
      activityUpgradePlusYear,
      activityUpgradeUltraYear,
      activityConfig,
      activitySegment,
      currentPackage
    });
    result = Math.round(originMoney * discount);
  }

  /**
   * 春季焕新活动期间价格修正
   */
  if (isShowActivity) {
    if (_timeMode === 'year' && result === 713) {
      result = 712
    } else if (_timeMode === 'year' && result === 1433) {
      result = 1432
    }
  }

  return result;
}

/**
 * 获取算力包金额
 * @param key 算力包类型
 * @returns 金额
 */
export const getPowerMoney = (key: keyof typeof powerPackage) => {
  return powerPackage[key].money;
}

// 获取套餐订阅周期文案
export function getSubscribeTimeLabel(key: keyof typeof userPackage) {
  if (key === "svip") {
    return packageTimeEnum["halfSubscribe"];
  }
  return packageTimeEnum["subscribe"];
}

// 获取订单价格
export function getOrderMoney(
  orderType: "member" | "power" | "teamPower",
  {
    powerPackageKey,
    memberPackageKey,
    timeMode,
    isShowActivity,
    isParticipatedPro,
    isParticipatedPlus,
    activityUpgradePlusMonth,
    activityUpgradeUltraMonth,
    activityUpgradePlusYear,
    activityUpgradeUltraYear,
    activityConfig,
    activitySegment,
    currentPackage,
    isSvipMonthlySubscribeB
  }: {
    powerPackageKey?: keyof typeof powerPackage;
    memberPackageKey?: keyof typeof vipPackage;
    timeMode?: keyof typeof packageTimeEnum;
    isShowActivity?: boolean;
    isParticipatedPro?: boolean;
    isParticipatedPlus?: boolean;
    activityUpgradePlusMonth?: boolean;
    activityUpgradeUltraMonth?: boolean;
    activityUpgradePlusYear?: boolean;
    activityUpgradeUltraYear?: boolean;
    activityConfig?: ActivityConfig;
    activitySegment?: UserSegment;
    currentPackage?: string;
    isSvipMonthlySubscribeB?: boolean;
  }
) {
  if (["power", "teamPower"].includes(orderType)) {
    const key = powerPackageKey as keyof typeof powerPackage;
    return powerPackage[key].money;
  }

  return getPackageMoney(memberPackageKey!, timeMode!, {
    isShowActivity,
    isParticipatedPro,
    isParticipatedPlus,
    activityUpgradePlusMonth,
    activityUpgradeUltraMonth,
    activityUpgradePlusYear,
    activityUpgradeUltraYear,
    activityConfig,
    activitySegment,
    currentPackage,
    isSvipMonthlySubscribeB
  });
}

/**
 * 获取订单详情
 * @param orderType 订单类型
 * @param param1 订单参数 powerPackageKey 算力包key memberPackageKey 会员包key timeMode 时间模式
 * @returns 订单详情
 */
export function getOrderDetails(
  orderType: "member" | "power" | "teamPower",
  {
    powerPackageKey,
    memberPackageKey,
    timeMode,
    /** 个人订阅/买断周期文案；缺省回退 packageTimeEnum（兼容旧入口） */
    periodLabel
  }: {
    powerPackageKey?: keyof typeof powerPackage;
    memberPackageKey?: keyof typeof vipPackage;
    timeMode?: keyof typeof packageTimeEnum;
    periodLabel?: string;
  }
) {
  if (["power", "teamPower"].includes(orderType)) {
    return `购买${getPowerPackageNumber(powerPackageKey!)}算力`;
  }

  const cycleText = periodLabel ?? packageTimeEnum[timeMode!];
  return `<span class="${memberPackageKey}"><span class="member-type">${vipPackage[memberPackageKey!].label}会员</span><span class="sub-content">-${cycleText
    }</span></span>`;
}

// 获取订单会员有效期
export function getMembershipPeriod(memberPackageKey: keyof typeof vipPackage, timeMode: keyof typeof packageTimeEnum) {
  const userStore = useUserStore();
  const timeFormat = "YYYY/MM/DD";

  let startTime = "";

  const [target] = userStore.packages.filter((item) => item.memberType === _vipPackageEnum[memberPackageKey]);
  startTime = target ? dayjs(target.endTime).format(timeFormat) : dayjs().format(timeFormat);

  let daysNumber = VipTimePackageMonthEnum[timeMode] * packageMonthDays;

  // const entTime = dayjs(startTime).add(target ? daysNumber : daysNumber - 1, 'day').format(timeFormat);
  const entTime = dayjs(startTime).add(daysNumber, "day").format(timeFormat);

  // return `${startTime}-${entTime}`;
  return `${dayjs(target?.startTime).format(timeFormat)} 至 ${entTime}`;
}

// 获取订阅、季度、年度天数
export function getMemberTimeModeDays(timeMode: keyof typeof packageTimeEnum) {
  if (timeMode === "halfSubscribe") {
    return packageHalfMonthDays;
  }
  return VipTimePackageMonthEnum[timeMode] * packageMonthDays;
}

/**
 * 会员套餐计费周期内发放的算力总量（用于「元/100算力」分母）
 * 季度/年度按周期内每月算力累加；SVIP 连续包半月为 4500。
 */
export function getMemberBillingPeriodPowerAmount(
  memberType: keyof typeof vipPackage,
  timeMode: keyof typeof packageTimeEnum
): number {
  if (memberType === "free") {
    return 0;
  }

  if (memberType === "svip" && timeMode === "halfSubscribe") {
    return vipPackage.svip.power / 2;
  }

  const monthlyPower = vipPackage[memberType].power;
  if (timeMode === "season") {
    return monthlyPower * VipTimePackageMonthEnum.season;
  }
  if (timeMode === "year") {
    return monthlyPower * VipTimePackageMonthEnum.year;
  }
  if (timeMode === "year_v1") {
    return monthlyPower * VipTimePackageMonthEnum.year_v1;
  }

  return monthlyPower;
}

/** 按售价与计费周期算力总量计算「元/100算力」展示值 */
export function formatMemberPerHundredPowerYuan(
  salePrice: number,
  memberType: keyof typeof vipPackage,
  timeMode: keyof typeof packageTimeEnum
): string {
  const powerTotal = getMemberBillingPeriodPowerAmount(memberType, timeMode);
  if (!powerTotal) {
    return "0.00";
  }

  return ((salePrice / powerTotal) * 100).toFixed(2);
}

/** 按售价与计费周期天数计算「元/天」展示值 */
export function formatMemberDailyPriceYuan(
  salePrice: number,
  timeMode: keyof typeof packageTimeEnum
): string {
  const days = getMemberTimeModeDays(timeMode);
  if (!days) {
    return "0.00";
  }

  return (salePrice / days).toFixed(2);
}

// 获取当前会员套餐是否可用
export function getMemberPackageUsable({ memberType, timeMode }: { memberType: keyof typeof vipPackage; timeMode: keyof typeof packageTimeEnum }) {
  const userStore = useUserStore();

  if (userStore.isSubscribeUser) {
    return true;
  }

  if (timeMode === "week" && !userStore.weekMemberUsable) {
    return false;
  }

  if (userStore.isOrderUser && ["halfSubscribe", "subscribe"].includes(timeMode)) {
    return false;
  }

  if (userStore.highestPackage === "svip" && memberType === "vip") {
    return false;
  }

  if (userStore.highestPackage === "vip" && memberType === "svip" && userStore.memberRemainingDays > getMemberTimeModeDays(timeMode)) {
    return false;
  }

  return true;
}

// 获取开通会员算力权益
export function getMemberPackagePowerLabel({
  memberType,
  timeMode
}: {
  memberType: keyof typeof vipPackage;
  timeMode: keyof typeof packageTimeEnum;
}) {
  if (timeMode === "week") {
    return `${packageTimeEnum["week"]}发放 1000 算力`;
  }

  if (timeMode === "halfSubscribe") {
    return `每半月发放 ${vipPackage.svip.power / 2} 算力`;
  }

  if (memberType === "vip") {
    return `每月发放 ${vipPackage.vip.power} 算力`;
  }

  if (memberType === "ultra") {
    return `每月发放 ${vipPackage.ultra.power} 算力`;
  }

  return `每月发放 ${vipPackage.svip.power} 算力`;
}

// 获取开通会员算力权益
export function getMemberPackagePowerLabelByEnterprise({
  memberType,
  timeMode
}: {
  memberType: keyof typeof vipPackage;
  timeMode: keyof typeof packageTimeEnum;
}) {
  if (timeMode === "week") {
    return `${packageTimeEnum["week"]}发放 1000 算力`;
  }

  if (timeMode === "halfSubscribe") {
    return `已发放 ${vipPackage.svip.power / 2} 算力`;
  }

  if (memberType === "vip") {
    return `已发放 ${vipPackage.vip.power} 算力`;
  }

  return `已发放 ${vipPackage.svip.power} 算力`;
}
